import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@environments/environment';
import {DeploymentEnvironment, IBusinessPartner} from '@service-and-repairs/awpintegrationlib';
import {ServiceCase} from '../../core/service-case/models/service-case';
import {ServiceCaseHolder} from '../../core/service-case/models/service-case-holder';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';
import {AwpAgentService} from './awp-agent.service';

/**
 * AWP Agent component that wraps the retail companion web component (`rcom-chat-widget`)
 * loaded via the Retail Companion CDN loader.
 *
 * This component:
 * - Is only rendered when `isAwpAgentEnabled()` returns true (DEV_LOCAL, DEV_TEST, INT).
 * - Declares the `<rcom-chat-widget>` element directly in the template with all required
 *   attributes as documented in the dcomp/frontend README.
 * - Loads the web component definition via the environment-specific CDN loader.
 * - Uses the CDN widget contract with the RC Backend proxy base URL.
 *
 * @see https://atc-github.azure.cloud.bmw/dcomp/frontend
 * @see https://atc.bmwgroup.net/confluence/spaces/RPAS/pages/6397208529/Retailer+Companion+Widget+Integration
 */
@Component({
  selector: 'app-awp-agent',
  templateUrl: './awp-agent.component.html',
  styleUrl: './awp-agent.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AwpAgentComponent implements OnInit {
  private readonly awpAgentService: AwpAgentService = inject(AwpAgentService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly userService: UserService = inject(UserService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);

  /** RC Backend base URL passed to the widget. */
  readonly backendApiBaseUrl: string = this.awpAgentService.getBackendApiBaseUrl();

  /** Widget locale; `null` removes the `locale` attribute so the widget uses its own default. */
  locale: string | null = null;

  /** JSON string passed as-is to CAAPI as `channelData`. Keys are case-sensitive. */
  channelData: string = '{}';

  /** JSON string passed to the widget as additional host context for MCP UI apps. Keys are case-sensitive. */
  businessContext: string = '{}';

  private user: UserData | null = null;

  ngOnInit(): void {
    this.userService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user: UserData): void => {
        this.user = user;
        this.locale = user?.getLocale() || null;
        this.updateWidgetContext();
      });

    this.serviceCaseHolder.activeServiceCaseChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.updateWidgetContext());

    if (this.isAwpAgentEnabled()) {
      void this.awpAgentService.loadWidgetLoader();
    }
  }

  /**
   * Returns whether the AWP Agent (retail companion) feature is enabled for the current deployment environment.
   * Currently enabled for DEV_LOCAL, DEV_TEST, and INT only.
   */
  isAwpAgentEnabled(): boolean {
    return environment.deploymentEnvironment === DeploymentEnvironment.DEV_LOCAL
      || environment.deploymentEnvironment === DeploymentEnvironment.DEV_TEST
      || environment.deploymentEnvironment === DeploymentEnvironment.INT;
  }

  private updateWidgetContext(): void {
    const user: UserData | null = this.user;
    const businessPartner: IBusinessPartner | undefined = user?.getBusinessPartner();
    const activeServiceCase: ServiceCase | undefined = this.serviceCaseHolder.getActiveCase();
    const activeServiceCaseId: string | undefined = activeServiceCase?.getExternalId() || undefined;
    const awpHost: string | undefined = window.location.host || undefined;
    const channelData: Record<string, unknown> = {
      'Auth-Dist-Partner': businessPartner?.getDistributionPartnerNumber() || undefined,
      'Auth-Outlet': businessPartner?.getOutletNumber() || undefined,
      'Auth-Country': user?.getB2XCountryCode() || undefined,
      'User-Business-Partner': businessPartner?.getBusinessPartnerId() || undefined,
      'User-Context': user?.getContext() || undefined,
      'User-Id': user?.getId() || undefined,
      'User-Name': user?.getName() || undefined,
      'Awp-Host': awpHost,
      'Language': user?.getLocale() || undefined,
      'Vin': activeServiceCase?.getVinLong() || undefined,
      'Active-Service-Case': activeServiceCaseId
    };
    const businessContext: Record<string, unknown> = {
      activeServiceCase: activeServiceCaseId,
      awpHost
    };
    this.channelData = JSON.stringify(channelData);
    this.businessContext = JSON.stringify(businessContext);
    this.changeDetectorRef.markForCheck();
  }
}
